import { ActionType, NetworkActionPrefix, RequestStatus, BusinessModelArea, MeetingStatus } from '../utils/constants';
import defaults from '../api/defaults';
import { deepAssign } from '../utils';
import {
    pipeReducers, combineReducers, networkObjectLoadReducer, networkPaginationLoadReducer, paginationSlicePushReducer, synchronizableEnumReducer,
    networkEntityLoadReducer, networkEntityAddReducer, networkEntityEditReducer, networkEntityDeleteReducer, networkActionReducer,
    sortableEntitiesLifecycleReducer, networkEntityEditAllReducer
} from '../utils/reducerFactory';

function index(state, action, id) {
    return id !== undefined ? (id = state.getIndexByID(id)) !== -1 ? id : state.length : undefined;
}

const keyVariables = {
    trendTopicIndex: (state, action) => index(state, action, action.trendTopicID),
    trendCommentIndex: (state, action) => index(state, action, action.trendCommentID),
    trendCategoryIndex: (state, action) => index(state, action, action.trendCategoryID),
    trendScopeIndex: (state, action) => index(state, action, action.trendScopeID),
    trendTopicTypeIndex: (state, action) => index(state, action, action.trendTopicTypeID)
};

export default combineReducers({
    topics: pipeReducers(
        networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_TREND_TOPICS_PREFIX, defaults.NetworkArray),
        combineReducers({
            Data: combineReducers({
                '@trendTopicIndex': pipeReducers(
                    networkEntityLoadReducer(NetworkActionPrefix.LOAD_TREND_TOPIC_PREFIX),
                    networkEntityAddReducer(NetworkActionPrefix.ADD_TREND_TOPIC_PREFIX, null, (state, action) => action.trendTopic),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_TREND_TOPIC_PREFIX, null, (state, action) => action.trendTopic),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_TREND_TOPIC_PREFIX),
                    networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_TREND_TOPICS_PREFIX, null, (state, action) => action.trendTopics),
                    combineReducers({
                        Comments: pipeReducers(
                            networkPaginationLoadReducer(NetworkActionPrefix.LOAD_TREND_COMMENTS_PREFIX, defaults.TrendTopicCommentPaginationSlice),
                            paginationSlicePushReducer(ActionType.ADD_TREND_COMMENT, defaults.TrendTopicCommentPaginationSlice),
                            combineReducers({
                                Data: combineReducers({
                                    '@trendCommentIndex': pipeReducers(
                                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_TREND_COMMENT_PREFIX),
                                        networkEntityAddReducer(NetworkActionPrefix.ADD_TREND_COMMENT_PREFIX, null, (state, action) => action.comment)
                                    )
                                }, keyVariables)
                            }, keyVariables)
                        ),
                        Custom(state = defaults.TrendTopicReport.Custom, action) {
                            switch (action.type) {

                                case ActionType.EDIT_TREND_NEW_COMMENT:
                                    return deepAssign(state, { newComment: action.comment });

                                case ActionType.ADD_TREND_COMMENT:
                                    return deepAssign(state, { newComment: defaults.TrendTopicComment });

                                default:
                                    return state;
                            }
                        }
                    })
                )
            }, keyVariables)
        }, keyVariables),
        (state, action) => {
            switch (action.type) {
                case ActionType.EDIT_ALL_TREND_TOPICS_SUCCESS:
                    return deepAssign(state, {
                        Data: state.Data.map(topic => action.trendTopics.getByID(topic.ID) || topic)
                    });

                case ActionType.DELETE_TREND_TOPIC_SUCCESS:
                    return deepAssign(state, {
                        Data: state.Data.map(topic => topic.ParentID === action.trendTopicID ? { ...topic, ParentID: defaults.TrendTopic.ParentID } : topic)
                    });

                case ActionType.DELETE_ALL_TREND_TOPIC_TYPES_SUCCESS:
                    return deepAssign(state, {
                        Data: state.Data.map(topic => action.trendTopicTypeIDs.includes(topic.TopicTypeID) ? { ...topic, TopicTypeID: defaults.TrendTopic.TopicTypeID } : topic)
                    });

                case ActionType.DELETE_MARKET:
                    return action.payload.status === RequestStatus.SUCCEEDED ?
                        deepAssign(state, {
                            Data: state.Data.map(topic => ({
                                ...topic,
                                MarketID: topic.MarketID === action.payload.entityID ? defaults.TrendTopic.MarketID : topic.MarketID
                            }))
                        }) :
                        state;

                case ActionType.EDIT_MEETING_STATUS_SUCCESS:
                    return action.statusID === MeetingStatus.ARCHIVED ?
                        deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } }) :
                        state;

                default:
                    return state;
            }
        }
    ),
    trendSettings: pipeReducers(
        networkActionReducer(NetworkActionPrefix.SEARCH_TREND_SETTINGS_FOR_COMPANY_PREFIX, defaults.TrendSettings),
        networkEntityEditReducer(NetworkActionPrefix.EDIT_TREND_SETTINGS_PREFIX, defaults.TrendSettings, (state, action) => action.trendSettings),
        (state, action) => {
            switch (action.type) {
                case ActionType.SEARCH_TREND_SETTINGS_FOR_COMPANY_SUCCESS:
                    return action.response ? deepAssign(state, action.response) : state;

                default:
                    return state;
            }
        }
    ),
    scopes: pipeReducers(
        networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_TREND_SCOPES_PREFIX, defaults.NetworkArray),
        sortableEntitiesLifecycleReducer('', {
            '@trendScopeIndex': pipeReducers(
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_TREND_SCOPES_PREFIX, null, (state, action) => action.trendScopes)
            )
        }, keyVariables),
        (state, action) => {
            switch (action.type) {
                case ActionType.EDIT_ALL_TREND_SCOPES_PENDING:
                    return deepAssign(state, {
                        Custom: { status: RequestStatus.PENDING }
                    });

                case ActionType.EDIT_ALL_TREND_SCOPES_SUCCESS:
                    let data = state.Data.map(scope => action.trendScopes.getByID(scope.ID) || scope);
                    data.sort((a, b) => a.Sort - b.Sort);

                    return deepAssign(state, {
                        Data: data,
                        Custom: { status: RequestStatus.SUCCEEDED }
                    });

                case ActionType.EDIT_ALL_TREND_SCOPES_FAIL:
                    return deepAssign(state, {
                        Custom: { status: RequestStatus.FAILED }
                    });

                default:
                    return state;
            }
        }),
    trendCategories: pipeReducers(
        networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_TREND_CATEGORIES_PREFIX, defaults.NetworkArray),
        sortableEntitiesLifecycleReducer('', {
            '@trendCategoryIndex': pipeReducers(
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_TREND_CATEGORIES_PREFIX, null, (state, action) => action.trendCategories)
            )
        }, keyVariables),
        (state, action) => {
            switch (action.type) {
                case ActionType.EDIT_ALL_TREND_CATEGORIES_PENDING:
                    return deepAssign(state, {
                        Custom: { status: RequestStatus.PENDING }
                    });

                case ActionType.EDIT_ALL_TREND_CATEGORIES_SUCCESS:
                    let data = state.Data.map(category => action.trendCategories.getByID(category.ID) || category);
                    data.sort((a, b) => a.Sort - b.Sort);

                    return deepAssign(state, {
                        Data: data,
                        Custom: { status: RequestStatus.SUCCEEDED }
                    });

                case ActionType.EDIT_ALL_TREND_CATEGORIES_FAIL:
                    return deepAssign(state, {
                        Custom: { status: RequestStatus.FAILED }
                    });

                default:
                    return state;
            }
        }),
    trendTopicTypes: pipeReducers(
        networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_TREND_TOPIC_TYPES_PREFIX, defaults.NetworkArray),
        networkActionReducer(NetworkActionPrefix.ADD_ALL_TREND_TOPIC_TYPES_PREFIX, null, (state, action) => action.trendTopicTypes),
        networkActionReducer(NetworkActionPrefix.EDIT_ALL_TREND_TOPIC_TYPES_PREFIX, null, (state, action) => action.trendTopicTypes),
        networkActionReducer(NetworkActionPrefix.DELETE_ALL_TREND_TOPIC_TYPES_PREFIX, null, (state, action) => action.trendTopicTypeIDs),
        combineReducers({
            Data: combineReducers({
                "@trendTopicTypeIndex": pipeReducers(
                )
            }, keyVariables)
        }),
        (state, action) => {
            switch (action.type) {
                case ActionType.ADD_ALL_TREND_TOPIC_TYPES_SUCCESS:
                    return deepAssign(state, {
                        Data: [...state.Data, ...action.trendTopicTypes.map((source, index) => ({ ...source, ID: action.response[index] }))]
                    });

                case ActionType.EDIT_ALL_TREND_TOPIC_TYPES_SUCCESS:
                    return deepAssign(state, {
                        Data: state.Data.map(trendTopicType => action.trendTopicTypes.getByID(trendTopicType.ID) || trendTopicType)
                    });

                case ActionType.DELETE_ALL_TREND_TOPIC_TYPES_SUCCESS:
                    return deepAssign(state, {
                        Data: state.Data.filter(trendTopicType => !action.trendTopicTypeIDs.includes(trendTopicType.ID))
                    });

                default:
                    return state;
            }
        }
    ),
    businessModelAreas: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_BUSINESS_MODEL_AREAS_PREFIX, defaults.NetworkArray, BusinessModelArea, 'BusinessModelArea')
});
